
import React from "react";
import { i18nManager } from "../../../i18n";
import * as ReactRouterDom from "react-router-dom";
import { CacheManager, Debugger, Schemes, Utils } from "../../../utils";
import { ApiResponseData, IStatus, ISupportRequest, IUser, SanitizedResponse, SupportRequestService } from "../../../services";
import { Button, ButtonManageRef, Classname, Column, DataManageRef, DateTimeInput, DateTimePickerEvent, DateTimePickerSelectionMode, Dropdown, FormGroup, Paginator, PaginatorPageElementOption, Popover, PopoverManageRef, Table, TextInput } from "@ronuse/noseur";

export type TicketsProps = {
    setTitle: (title: string) => void;
    setShowBreadCrumb: (show: boolean) => void;
};

function Tickets(props: TicketsProps) {
    const { platform } = ReactRouterDom.useParams();
    const labels = i18nManager.Labels.support.ticket.tickets;
    const activePlatform = CacheManager.getPlatform(platform);
    const ticketsDataManageRef = React.useRef<DataManageRef>(null);
    const searchButtonManageRef = React.useRef<ButtonManageRef>(null);
    const supportRequestService = SupportRequestService.getInstance();
    const ticketPopOverManageRef = React.useRef<PopoverManageRef>(null);
    const [activeSupportRequest, setActiveSupportRequest] = React.useState<ISupportRequest>();
    const [searchParams, setSearchParams] = ReactRouterDom.useSearchParams(window.location.search);
    const [supportRequests, setSupportRequests] = React.useState<ApiResponseData<ISupportRequest>>({});

    React.useEffect(() => {
        fetchTickets();
        props.setTitle(labels.title);
        props.setShowBreadCrumb(false);
    }, []);

    return (<div className="tickets">
        <FormGroup scheme={Schemes.RIVTN_HERA} className="filter">
            <TextInput defaultValue={searchParams.get("title") ?? ""} className="title" placeholder={labels.filter.title.placeholder} onInputComplete={(v) => Utils.updateSearchParams("title", v, setSearchParams)} />
            <DateTimeInput formControlProps={{ className: "dropdown" }} placeholder={labels.filter.date.placeholder} selectionMode={DateTimePickerSelectionMode.RANGE}
                onSelectDate={(options: DateTimePickerEvent) => {
                    console.log("OPTIONS", options);
                }}
                rightContent={"fa fa-angle-down"} />
            <Dropdown formControlProps={{ className: "dropdown" }} className="dropdown" placeholder={labels.status} options={labels.filter.statuses}
                onSelectOption={(status: any) => {
                    Utils.updateSearchParams("status", status.value, setSearchParams);
                    return true;
                }} selectedOptionIndex={labels.filter.statuses.findIndex((status) => status.value === searchParams.get("status"))} fill />
            <Button leftIcon="fa fa-search" text={labels.search} loadingProps={Utils.BUTTON_LOADING_PROPS} manageRef={searchButtonManageRef} onClick={fetchTickets} raised />
        </FormGroup>
        <Table manageRef={ticketsDataManageRef} className="tickets-table" data={supportRequests.content} totalRecords={supportRequests.total_elements} dataRefreshKeys={["status", "external_id"]}
            loadingState={Utils.LOADING_STATE} emptyState={<div className="es" style={{ marginTop: 20 }}></div>} rowProps={{ className: "tickets-row" }}>
            <Column className="title" dataKey="title" header={labels.table.columns.title} />
            <Column className="created_at" dataKey="created_at" header={labels.table.columns.date} template={(date: string) => Utils.formatToFineDate(date)} />
            <Column className="tp" dataKey="participants" header={labels.table.columns.participants} template={(participants: IUser[]) => (<div className="tickets-participants">
                {participants.slice(0, 3).map((participant) => (<img key={participant.external_id} alt={participant.first_name} src={participant.profile_picture_location} />))}
                {participants.length > 3 && <span className="badge">+{(participants.length - 3)}</span>}
            </div>)} />
            <Column className="st" dataKey="status" header={labels.table.columns.status} template={(status: IStatus) => renderStatus(status)} />
            <Column className="actions" template={(supportRequest: ISupportRequest) => (<>
                <i className="fa fa-ellipsis" onClick={(e: any) => {
                    setActiveSupportRequest(supportRequest);
                    ticketPopOverManageRef.current?.toggle(e);
                }} />
            </>)} />
        </Table>
        <Paginator totalRecords={supportRequests.total_elements} rowsPerPage={supportRequests.size} initialPage={parseInt(searchParams.get("page") ?? "1")} template={{
            layout: "PreviousPageElement PreviousPagesElement PageElements NextPagesElement NextPageElement",
            pageElement: (option: PaginatorPageElementOption) => (<Button text={option.label} onClick={option.onClick} className="paginator-button" scheme={Schemes.RIVTN_HERA} fillOnHover textOnly={!option.active} />),
            previousPageElement: (option: PaginatorPageElementOption) => (<Button onClick={option.onClick} className="paginator-button" scheme={Schemes.RIVTN_HERA} leftIcon="fa fa-angle-left" fillOnHover textOnly />),
            nextPageElement: (option: PaginatorPageElementOption) => (<Button onClick={option.onClick} className="paginator-button" scheme={Schemes.RIVTN_HERA} leftIcon="fa fa-angle-right" fillOnHover textOnly />),
        }} onPageChange={(e) => Utils.updateSearchParams("page", e.currentPage, setSearchParams, fetchTickets)} />


        <Popover manageRef={ticketPopOverManageRef} className="popover" dismissOnClick>
            <a href={`/${platform}/support/ticket/${activeSupportRequest?.external_id}`}>{labels.table.popover.view}</a>
            {(activeSupportRequest?.status === IStatus.OPENED || activeSupportRequest?.status === IStatus.RE_OPENED) && (<>
                <span onClick={() => updateTicketStatus(IStatus.RESOLVED)}>{labels.table.popover.mark_resolve}</span>
                <span className="danger" onClick={() => updateTicketStatus(IStatus.CLOSED)}>{labels.table.popover.close_ticket}</span>
            </>)}
            {activeSupportRequest?.status === IStatus.CLOSED && (<>
                <span onClick={() => updateTicketStatus(IStatus.RE_OPENED)}>{labels.table.popover.reopen}</span>
            </>)}
        </Popover>
    </div>);

    function renderStatus(status: IStatus) {
        let lowercaseValue = (status.toLowerCase());
        let className = Classname.build("status", lowercaseValue);
        return (<span className={className}>{SupportRequestService.getStatusLabel(status)}</span>);
    }

    async function fetchTickets() {
        if (!activePlatform) return;
        searchButtonManageRef.current?.setLoadingState(true);
        supportRequestService.querySupportRequests({ "platform.external_id": activePlatform?.external_id!, ...Utils.normalizeUrlParams(searchParams, {}, { size: 10 }) }).then(({ sanitized }: SanitizedResponse<any>) => {
            Debugger.log("Tickets:useEffect:querySupportRequests", sanitized);
            setSupportRequests(sanitized);
        }).catch((error: SanitizedResponse<any>) => {
            Utils.reportError(error);
        }).finally(() => {
            ticketsDataManageRef.current?.setLoadingState(false);
            searchButtonManageRef.current?.setLoadingState(false);
        });
    }

    function updateTicketStatus(status: IStatus) {
        if (!activeSupportRequest?.external_id) return;
        ticketsDataManageRef.current?.setLoadingState(true);
        supportRequestService.updateSupportTicketStatus(activeSupportRequest.external_id, status).then(({ sanitized }: SanitizedResponse<any>) => {
            Debugger.log("Tickets:updateTicketStatus:updateSupportTicketStatus:success", sanitized);
        }).catch((error: SanitizedResponse<any>) => {
            Utils.reportError(error);
            Debugger.error("Tickets:updateTicketStatus:updateSupportTicketStatus:error", error);
        }).finally(() => {
            fetchTickets();
        });
    }

}

export default Tickets;
